import { Navbar } from "@/components/landing/Navbar";
import { FooterSection } from "@/components/landing/FooterSection";
import { Reveal } from "@/components/Reveal";
import { buildProviderSlug } from "@/lib/provider-slug";
import { MapPin } from "lucide-react";

export type IntentShellProps = {
 title: string;
 description: string;
 h1: string;
 intro: string;
 emptyMessage: string;
 providers: Array<{
 id: string;
 public_id?: string | null;
 name: string | null;
 credentials?: string | null;
 practice?: string | null;
 specialty?: string | null;
 city?: string | null;
 state?: string | null;
 }>;
 breadcrumbs: Array<{ label: string; href?: string }>;
};

export function IntentPageShell({ h1, intro, emptyMessage, providers, breadcrumbs }: IntentShellProps) {
 return (
 <div className="min-h-screen bg-background">
 <Navbar />
 <main className="mx-auto max-w-[1200px] px-6 pt-32 md:pt-24 pb-16">
 <nav className="text-sm text-muted-foreground mb-6" aria-label="Breadcrumb">
 {breadcrumbs.map((b, i) => (
 <span key={i}>
 {b.href ? (
 <a href={b.href} className="hover:text-primary transition-colors">{b.label}</a>
 ) : (
 <span className="text-foreground">{b.label}</span>
 )}
 {i < breadcrumbs.length - 1 ? <span className="mx-2">/</span> : null}
 </span>
 ))}
 </nav>

 <header className="mb-8">
 <p className="text-[12px] uppercase tracking-[0.14em] text-muted-foreground">
 {providers.length > 0 ? `${providers.length} providers found` : "No providers found"}
 </p>
 <h1 className="font-display text-[28px] font-semibold tracking-tight text-foreground mt-2">{h1}</h1>
 <p className="mt-3 text-[14px] text-muted-foreground max-w-2xl">{intro}</p>
 </header>

 {providers.length === 0 ? (
 <div className="card-surface p-10 text-center text-[14px] text-muted-foreground rounded-md">
 {emptyMessage}
 </div>
 ) : (
 <Reveal from="up" delay={60}>
 <ul className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-3 mb-12">
 {providers.map((p) => {
 const slug = buildProviderSlug(p as any);
 const name = (p.name || p.practice || p.specialty || "Provider").trim();
 const creds = (p.credentials || "").trim();
 return (
 <li key={p.id} className="flex items-center gap-2 text-[14px] min-w-0">
 <MapPin className="h-3.5 w-3.5 shrink-0 text-primary" aria-hidden />
 <span className="truncate">
 <a href={`/providers/${slug}`} className="text-muted-foreground hover:text-primary transition-colors">
 {creds ? `${name}, ${creds}` : name}
 </a>
 {p.city && p.state ? (
 <span className="text-muted-foreground/70">, {p.city}, {p.state}</span>
 ) : null}
 </span>
 </li>
 );
 })}
 </ul>
 </Reveal>
 )}
 </main>
 <FooterSection />
 </div>
 );
}
